<template>
  <div>
    <div
      class="alert text-center"
      :class="numberOfOptionsClass"
      v-if="item.config.max_options || item.config.min_options"
    >
      {{ chooseOptionsText }}
      {{ $t('word.selected') }}:

      <i class="fa fa-thumbs-up mr-1" v-if="numberOfOptionsValid"></i>
      <strong>{{selectedOptions.length}}</strong>
    </div>
    <div
      v-if="asAnswer"
      class="d-flex flex-column"
      :class="dirClass"
    >
      <div
        class="alert alert-secondary alert-checkbox container-fluid text-center"
        :class="{'alert-dark-secondary': selectedOptions.includes(option.value)}"
        v-for="option in options"
        :key="option.value"
      >
        <label
          class="m-0 form-check form-check-checkbox"
          :class="{'p-1': small}"
        >
          <span
            v-if="option.image"
            class="mr-1"
          >
            <img
              class="img-item-option"
              :src="option.image"
              :alt="option.value"
            />
          </span>
          <span v-else>
            <i class="fa fa-check" v-if="selectedOptions.includes(option.value)"></i>
          </span>
          {{ option.value }}
        </label>
      </div>
      <div
        class="alert alert-secondary container-fluid text-center alert-dark-secondary"
        v-if="valueAlt"
        :class="{'p-1': small}"
      >
        <small class="text-sm-center">{{ $t('phrase.else_namely') }}:</small> <br/>
        {{ valueAlt }}
      </div>
    </div>
    <div
      v-else
      class="d-flex flex-column"
      :class="dirClass"
    >
      <div
        class="alert alert-secondary alert-checkbox container-fluid text-center"
        :class="optionClass(option.value)"
        v-for="(option, index) in options"
        :key="option.value"
        :id="itemId(index)"
        @click="toggleOption(option.value)"
      >
        <label
          class="m-0 form-check form-check-checkbox"
          :class="{clickable: !exclusiveResponseSelected}"
          type="radio"
        >
          <span v-if="!option.image">
            <i class="fa fa-check" v-if="selectedOptions.includes(option.value )"></i>
          </span>

          <hr
            class="d-none d-md-block divider"
            v-if="researchItem.item.config.direction === 'horizontal'"
          />
          <span
            v-if="option.image"
            class="mr-1"
          >
          <img
            class="img-item-option"
            :src="option.image"
            :alt="option.value"
          />
          </span>
          {{ option.value }}
        </label>
      </div>
    </div>

    <div
      class="alert alert-secondary alert-checkbox text-center container-fluid"
      :class="{'alert-dark-secondary': exclusiveResponseSelected}"
      v-if="researchItem.item.config.exclusive_response"
      @click="toggleExclusiveResponse"
    >
      <div class="m-0 form-check form-check-checkbox clickable">
        <i class="fa fa-check" v-if="exclusiveResponseSelected"></i>
        {{ $t(EXCLUSIVE_RESPONSE_OPTIONS[researchItem.item.config.exclusive_response]) }}
      </div>
    </div>

    <div
      v-if="!asAnswer && (researchItem.item.config.allow_other || researchItem.answer.value_alt)"
    >
      <div
        class="alert alert-secondary alert-checkbox container-fluid text-center font-italic"
        :class="otherClass"
        @click="toggleOther"
      >
        {{ $t('phrase.else_namely') }}...
      </div>
      <div
        v-if="otherEnabled"
      >
        <v-text-input
          v-model="researchItem.answer.value_alt"
          id="review-research-value-alt"
          :auto-focus="true"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { shuffle } from '@/common/utils/arrays';
import TextInput from '@/common/components/inputs/TextInput.vue';
import { EXCLUSIVE_RESPONSE_OPTIONS } from '@/common/services/items';
import { GenericItemMixin } from '@/reviewer/mixins';

export default {
  name: 'ItemCheckboxes',
  components: {
    'v-text-input': TextInput,
  },
  mixins: [
    GenericItemMixin,
  ],
  data() {
    return {
      EXCLUSIVE_RESPONSE_OPTIONS,
      selectedOptions: [],
      otherEnabled: false,
      options: null,
    };
  },
  methods: {
    getOptions() {
      return this.researchItem.options;
    },
    itemId(index) {
      return this.idPrefix ? `${this.idPrefix}-item-check-box-option-${index}` : `item-check-box-option-${index}`;
    },
    optionClass(optionValue) {
      return {
        'alert-dark-secondary': this.selectedOptions.includes(optionValue),
        'alert-disabled': this.exclusiveResponseSelected,
      };
    },
    toggleOther() {
      if (this.exclusiveResponseSelected) {
        this.otherEnabled = false;
        return;
      }

      if (this.otherEnabled) {
        this.researchItem.answer.value_alt = '';
      }
      this.otherEnabled = !this.otherEnabled;
    },
    toggleExclusiveResponse() {
      this.otherEnabled = false;
      this.researchItem.answer.value_alt = null;
      if (!this.exclusiveResponseSelected) {
        this.selectedOptions = [this.researchItem.item.config.exclusive_response];
      } else {
        this.researchItem.answer.value = [];
        this.selectedOptions = [];
      }
    },
    toggleOption(optionValue) {
      if (this.exclusiveResponseSelected) {
        return;
      }

      if (this.selectedOptions.includes(optionValue)) {
        this.selectedOptions = this.selectedOptions.filter((item) => item !== optionValue);
      } else {
        this.selectedOptions.push(optionValue);
      }
    },
  },
  watch: {
    selectedOptions() {
      this.researchItem.answer.value = this.selectedOptions;
    },
  },
  computed: {
    chooseOptionsText() {
      if (!this.item.config.min_options) {
        return this.$t('sentence.choose_max_options', { count: this.item.config.max_options });
      } if (!this.item.config.max_options) {
        return this.$t('sentence.choose_min_options', { count: this.item.config.min_options });
      } if (this.item.config.min_options === this.item.config.max_options) {
        return this.$t('sentence.choose_exact_options', { count: this.item.config.max_options });
      }
      return this.$t(
        'sentence.choose_between_options',
        { countMax: this.item.config.max_options, countMin: this.item.config.min_options },
      );
    },
    numberOfOptionsClass() {
      return this.numberOfOptionsValid ? 'alert-success' : 'alert-danger';
    },
    numberOfOptionsValid() {
      if (this.item.config.min_options !== null) {
        if (this.item.config.min_options > this.selectedOptions.length) {
          return false;
        }
      }
      if (this.item.config.max_options !== null) {
        if (this.item.config.max_options < this.selectedOptions.length) {
          return false;
        }
      }
      return true;
    },
    otherClass() {
      return {
        'alert-dark-secondary': this.otherEnabled,
        'alert-disabled': this.exclusiveResponseSelected,
        clickable: !this.exclusiveResponseSelected,
      };
    },
    exclusiveResponseSelected() {
      return this.selectedOptions.includes(this.researchItem.item.config.exclusive_response);
    },
    value() {
      return this.researchItem.answer.value || [];
    },
    valueAlt() {
      return this.researchItem.answer.value_alt;
    },
    item() {
      return this.researchItem.item;
    },
    dirClass() {
      if (!this.small) {
        return { 'flex-lg-row': this.item.config.direction === 'horizontal' };
      }
      return {};
    },
  },
  created() {
    if (this.researchItem.answer.value) {
      this.selectedOptions = this.researchItem.answer.value;
    }

    if (this.valueAlt) {
      this.otherEnabled = true;
    }

    if (this.item.config.random) {
      this.options = shuffle(this.getOptions());
    } else {
      this.options = this.getOptions();
    }
  },
};
</script>

<style scoped>
.alert-checkbox {
  padding: 0;
  min-height: 61px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.alert-disabled {
  color: #aaadaf;
}

.form-check-checkbox {
  padding: 20px;
}

.divider {
  margin: 5px;
}
</style>
